import { useEffect, useState } from "react";
import { api, formatMoney, formatDate } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Landmark, Receipt, TrendingUp, Plus } from "lucide-react";
import RescheduleBillModal from "@/components/RescheduleBillModal";
import ReconcileBillsFlow from "@/components/ReconcileBillsFlow";
import MonthlySummaryOptIn from "@/components/MonthlySummaryOptIn";
import { useAuth } from "@/lib/auth-context";
import { useBillsBadge } from "@/lib/bills-badge-context";

// Dashboard sub-components — extracted out of this file to keep the
// top-level page composition readable. See /app/frontend/src/pages/app/dashboard/.
import GettingStartedCard from "./dashboard/GettingStartedCard";
import NextTightWeekBanner from "./dashboard/NextTightWeekBanner";
import NextMonthsStrip from "./dashboard/NextMonthsStrip";
import NextRollingWindowsStrip from "./dashboard/NextRollingWindowsStrip";
import CurrentPayPeriodSection from "./dashboard/CurrentPayPeriodSection";
import EditIncomeEventModal from "./dashboard/EditIncomeEventModal";
import { MiniRow } from "./dashboard/shared";

export default function Dashboard() {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const { refresh: refreshBillsBadge } = useBillsBadge();
  const [data, setData] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [pending, setPending] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [monthWindow, setMonthWindow] = useState(null);
  // Rolling 3-month planning from /api/forecast/monthly. Drives the
  // "Your Next Three Months" strip that replaces "Your next pay periods"
  // when the user toggles Dashboard to Month view.
  const [monthlyForecast, setMonthlyForecast] = useState([]);
  // Rolling 12-week horizon for the "Next tight week" banner. Cheap call —
  // powers a single top-of-page nudge that surfaces the closest amber/red
  // week from Year at a Glance without making users navigate.
  const [yearShort, setYearShort] = useState(null);
  // Snapshots the current user has created, used to show a "N views" pill
  // next to the Share button on the Next Tight Week banner. Kept as a live
  // list so recently-created snapshots surface immediately after saving.
  const [mySnapshots, setMySnapshots] = useState([]);
  const [bills, setBills] = useState([]);
  const [fundingSources, setFundingSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  // Inline income edit popover state — populated when the user clicks an
  // income row on any future pay-period card.
  const [editingIncomeEvent, setEditingIncomeEvent] = useState(null);
  // Untouched sample-demo row count. When > 0, we show a persistent "You're
  // viewing sample data · Clear it in Settings" banner so users never wonder
  // where those rows came from. Fetched alongside the initial forecast.
  const [demoTotal, setDemoTotal] = useState(0);
  const [showDemoBanner, setShowDemoBanner] = useState(true);
  // One-shot welcome note for users landing on the Dashboard immediately
  // after finishing onboarding. The Onboarding page sets a localStorage
  // flag; the Dashboard reads + clears it here so the note appears exactly
  // once and doesn't nag on subsequent visits.
  const [showFirstVisitNote, setShowFirstVisitNote] = useState(() => {
    try { return window.localStorage.getItem("wmrb_first_dash_visit") === "1"; }
    catch { return false; }
  });
  const dismissFirstVisitNote = () => {
    setShowFirstVisitNote(false);
    try { window.localStorage.removeItem("wmrb_first_dash_visit"); } catch { /* ignore */ }
  };
  // Lifted from CurrentPayPeriodSection so BOTH the forecast card AND the
  // "Your next..." strip below share the same toggle state.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = window.localStorage.getItem("wmrb_forecast_view");
      if (v === "month" || v === "next-30" || v === "pay-period") return v;
      return "pay-period";
    } catch { return "pay-period"; }
  });
  const changeViewMode = (m) => {
    setViewMode(m);
    try { window.localStorage.setItem("wmrb_forecast_view", m); } catch { /* ignore */ }
  };

  // Re-fetch the forecast slices that depend on bill assignments/amounts.
  // Called after any bill mutation (reschedule, status change, etc.) so
  // "Your next pay periods" reflects the new due dates immediately without
  // requiring a full page reload.
  const refreshForecast = async () => {
    try {
      const [d, pp, p, b, i, mw, mo, yr, snaps] = await Promise.all([
        api.get("/forecast/dashboard"),
        api.get("/forecast/pay-periods"),
        api.get("/pending-payments"),
        api.get("/bills"),
        api.get("/incomes"),
        api.get("/forecast/month"),
        api.get("/forecast/monthly?months=4"),
        api.get("/forecast/year?weeks=12"),
        api.get("/snapshots/mine/list"),
      ]);
      setData(d.data);
      setPayPeriods(pp.data);
      setPending(p.data);
      setBills(b.data);
      setIncomes(i.data);
      setMonthWindow(mw.data);
      setMonthlyForecast(mo.data);
      setYearShort(yr.data);
      setMySnapshots(snaps.data?.items || []);
      refreshBillsBadge();
    } catch (e) {
      // Non-fatal — keep the stale state and let the user retry via UI.
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [d, i, p, pp, b, fs, mw, mo, yr, snaps, demoStatus] = await Promise.all([
          api.get("/forecast/dashboard"),
          api.get("/incomes"),
          api.get("/pending-payments"),
          api.get("/forecast/pay-periods"),
          api.get("/bills"),
          api.get("/funding-sources"),
          api.get("/forecast/month"),
          api.get("/forecast/monthly?months=4"),
          api.get("/forecast/year?weeks=12"),
          api.get("/snapshots/mine/list"),
          api.get("/onboarding/demo-status"),
        ]);
        setData(d.data);
        setIncomes(i.data);
        setPending(p.data);
        setPayPeriods(pp.data);
        setBills(b.data);
        setFundingSources(fs.data);
        setMonthWindow(mw.data);
        setMonthlyForecast(mo.data);
        setYearShort(yr.data);
        setMySnapshots(snaps.data?.items || []);
        setDemoTotal(demoStatus.data?.total || 0);
        // Note: previous "You have N pending bills" floating toast was
        // removed here — same information now surfaces inline as counted
        // alert links in the Reconcile Card at the bottom of the forecast.
      } finally { setLoading(false); }
    })();
  }, []);

  // Scroll to a section when navigating with a hash (e.g. #your-next-pay-periods
  // from the Back button on the pay-period bills detail page).
  useEffect(() => {
    if (loading) return;
    const hash = location.hash?.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [loading, location.hash]);

  // Refetch when the Dashboard route becomes active OR the tab regains focus.
  useEffect(() => {
    if (loading) return;
    refreshForecast();
  }, [location.pathname, loading]);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") refreshForecast(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  if (loading) {
    // If we just came from clicking "Explore sample demo data" during
    // onboarding, replace the generic loading line with a friendlier
    // message that sets expectations for what they're about to see.
    let demoLoad = false;
    try { demoLoad = window.sessionStorage.getItem("wmrb_demo_just_loaded") === "1"; } catch { /* ignore */ }
    if (demoLoad) {
      try { window.sessionStorage.removeItem("wmrb_demo_just_loaded"); } catch { /* ignore */ }
      return (
        <div className="max-w-md mx-auto mt-16 text-center" data-testid="dashboard-loading-demo">
          <div className="inline-block w-8 h-8 border-2 border-[#4A6148] border-t-transparent rounded-full animate-spin mb-4" aria-hidden="true" />
          <div className="font-heading text-lg text-[#2C302E]">
            One moment — loading your sample data
          </div>
          <div className="text-sm text-[#59605C] mt-2 leading-relaxed">
            So you can play around with it and get a feel for how the app works.
          </div>
        </div>
      );
    }
    return <div className="text-[#59605C]" data-testid="dashboard-loading">Loading your forecast…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero — sets the mental model for the whole page. Written in a
          reassuring, plain-English tone so first-time users understand
          exactly what "real balance" means before they touch anything. */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-2xl">
          <h1 className="font-heading text-3xl md:text-5xl font-semibold text-[#2C302E] tracking-tight" data-testid="dashboard-hero-title">
            What&apos;s Your Balance?
          </h1>
          <p className="mt-4 text-[#3E4340] leading-relaxed text-[15px]" data-testid="dashboard-hero-pitch">
            Find money problems before they happen — no more stressing about the next two weeks or making it through the month.
          </p>
          <p className="mt-3 text-[15px] leading-relaxed text-[#2C302E]" data-testid="dashboard-hero-answer">
            <span className="font-semibold">The answer:</span> Follow the three steps below to look into the future and see how much money you&apos;ll have left after you pay all your bills.
          </p>
        </div>
        <StatusBadge status={data.monthly_status} size="md" testId="dashboard-status" showHint />
      </div>

      {/* First-visit Welcome Card — a one-time educational card that lays
          out the 3-step mental model users will interact with every day.
          Triggered by the `wmrb_first_dash_visit` flag set at the end of
          onboarding and removed the instant they dismiss, so it never
          nags on subsequent visits. Upgraded Feb 2026 from the earlier
          short "This is your Cash Flow Forecast" banner. */}
      {showFirstVisitNote && (
        <div
          className="card-soft p-6 sm:p-7 bg-gradient-to-br from-[#EEF3EC] to-[#F5F1E9] border-2 border-[#B8CFB1]"
          data-testid="dashboard-welcome-card"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-white text-[#3B5B3E] flex items-center justify-center shrink-0" aria-hidden="true">
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E]">Welcome</div>
              <h2 className="font-heading text-xl sm:text-2xl text-[#2C302E] mt-0.5 font-semibold">
                Welcome to What&rsquo;s Your Balance
              </h2>
              <p className="text-sm text-[#59605C] mt-2">
                Here&rsquo;s how it works in 30 seconds:
              </p>
            </div>
            <button
              type="button"
              onClick={dismissFirstVisitNote}
              className="text-[#8A918D] hover:text-[#2C302E] text-2xl leading-none shrink-0 -mt-1"
              aria-label="Dismiss welcome card"
              data-testid="dashboard-welcome-dismiss"
            >×</button>
          </div>

          {/* The 3 steps, each with its own numbered chip + icon + short
              copy. Grid stacks on mobile, goes 3-across from md+ so
              tablets/desktops get the compact "at-a-glance" read. */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              {
                num: 1, Icon: Landmark, title: "Bank & Cash",
                body: "Enter what's actually in your accounts right now.",
                bg: "bg-[#EFF3F0]", chip: "bg-[#4A6148] text-white",
              },
              {
                num: 2, Icon: Receipt, title: "Bills & Planned Purchases",
                body: "Everything you owe — recurring bills, one-off planned buys.",
                bg: "bg-[#FBEEEA]", chip: "bg-[#7A2E24] text-white",
              },
              {
                num: 3, Icon: TrendingUp, title: "Money Coming In",
                body: "Your paychecks + any extra income.",
                bg: "bg-[#EDF3EA]", chip: "bg-[#1F6B2E] text-white",
              },
            ].map(({ num, Icon, title, body, bg, chip }) => (
              <div
                key={num}
                className={`${bg} rounded-2xl border-2 border-white/70 p-4 flex flex-col gap-2`}
                data-testid={`dashboard-welcome-step-${num}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`${chip} w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold`} aria-hidden="true">{num}</span>
                  <Icon size={16} strokeWidth={2} className="text-[#2C302E]" aria-hidden="true" />
                  <span className="text-sm font-semibold text-[#2C302E]">{title}</span>
                </div>
                <p className="text-xs text-[#59605C] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          {/* Payoff line + CTA to dismiss and start using the app. */}
          <div className="mt-5 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <p className="text-sm text-[#2C302E]">
              We do the math so you always know{" "}
              <span className="font-semibold text-[#3B5B3E]">what you&rsquo;ll actually have left.</span>
            </p>
            <button
              type="button"
              onClick={dismissFirstVisitNote}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#4A6148] text-white hover:bg-[#3B5B3E] transition-colors self-end sm:self-auto"
              data-testid="dashboard-welcome-cta"
            >
              Got it — take me to my forecast
            </button>
          </div>
        </div>
      )}

      {/* Sample-data banner — persistent while any untouched demo rows
          remain, so users always know their forecast is based on made-up
          numbers. Clicking "Clear it in Settings" jumps them right to the
          removal button. Dismissible for the session so it doesn't nag
          during active exploration. */}
      {demoTotal > 0 && showDemoBanner && (
        <div
          className="card-soft p-4 bg-[#FFF6E4] border border-[#E9D69F] flex items-start gap-3"
          data-testid="dashboard-demo-banner"
        >
          <div className="w-8 h-8 rounded-full bg-white text-[#8C5D30] flex items-center justify-center shrink-0 mt-0.5">
            <Sparkles size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8C5D30]">Sample data</div>
            <div className="text-sm text-[#2C302E] mt-0.5 leading-relaxed">
              You&rsquo;re exploring with {demoTotal} sample row{demoTotal === 1 ? "" : "s"}. Ready to enter your own?{" "}
              <Link to="/app/settings" className="font-semibold text-[#8C5D30] underline decoration-dotted underline-offset-2 hover:text-[#7A4E1F]" data-testid="dashboard-demo-banner-clear">
                Clear sample data in Settings
              </Link>. Anything you&rsquo;ve edited stays safe.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowDemoBanner(false)}
            className="text-[#8A918D] hover:text-[#2C302E] text-lg leading-none shrink-0"
            aria-label="Hide banner for this session"
            data-testid="dashboard-demo-banner-dismiss"
          >×</button>
        </div>
      )}

      {/* First-time setup card — shows only until the user has at least
          one account, one bill, and one income source. */}
      {(fundingSources.length === 0 || bills.length === 0 || incomes.length === 0) && (
        <GettingStartedCard
          hasAccounts={fundingSources.length > 0}
          hasBills={bills.length > 0}
          hasIncome={incomes.length > 0}
        />
      )}

      {/* Next Tight Week Banner — surfaces the closest amber/red week from
          Year at a Glance so users see the crunch coming without leaving
          the Dashboard. Hidden when everything within the horizon is safe. */}
      {yearShort && (
        <NextTightWeekBanner
          data={yearShort}
          mySnapshots={mySnapshots}
          onSnapshotCreated={(snap) => setMySnapshots((prev) => [snap, ...prev])}
        />
      )}

      {/* Current Pay Period — Renders as TWO stacked cards (Cash Flow
          Forecast + Reconcile / Bill list) so bills read as a visually
          separate action zone below the forecast. */}
      <CurrentPayPeriodSection
        payPeriods={payPeriods}
        monthWindow={monthWindow}
        viewMode={viewMode}
        onViewModeChange={changeViewMode}
        fundingSources={fundingSources}
        bills={bills}
        pending={pending}
        incomes={incomes}
        user={user}
        onReschedule={(b) => setRescheduling(b)}
        onOpenReconcile={() => setReconciling(true)}
        onSourceUpdated={(updated) => {
          setFundingSources((prev) => prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)));
          refreshForecast();
        }}
        onIncomeChanged={(updated, mode) => {
          setIncomes((prev) => {
            if (mode === "delete") return prev.filter((x) => x.id !== updated.id);
            if (mode === "create") return [...prev, updated];
            return prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x));
          });
          refreshForecast();
        }}
        onBillUpdated={(updated) => {
          setBills((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
          refreshForecast();
        }}
        onPendingChanged={() => refreshForecast()}
        onRefresh={() => refreshForecast()}
      />

      <MonthlySummaryOptIn me={user} onUpdated={(u) => setUser && setUser(u)} />

      <div id="your-next-pay-periods" className="card-soft p-6 scroll-mt-24" data-testid="dashboard-pay-periods-section">
        {viewMode === "month" ? (
          <NextMonthsStrip
            monthlyForecast={monthlyForecast}
            bills={bills}
            incomes={incomes}
            fundingSources={fundingSources}
            onOpenIncomeEvent={setEditingIncomeEvent}
          />
        ) : viewMode === "next-30" ? (
          /* 30-day rolling progression: today→+30, +30→+60, +60→+90. Each
             card mirrors the pay-period card visual language so the strip
             is consistent whichever mode the user picks in the top toggle. */
          <NextRollingWindowsStrip
            bills={bills}
            incomes={incomes}
            fundingSources={fundingSources}
            onOpenIncomeEvent={setEditingIncomeEvent}
          />
        ) : (
          /* Pay-period view — the two/three upcoming pay periods immediately
             ahead. */
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">Ahead</div>
                <h2 className="font-heading text-xl font-medium text-[#2C302E]">Your next pay periods</h2>
                <p className="text-xs text-[#59605C] mt-1">Only future pay periods. Each card shows the pay-period window, the paychecks landing in it, the bills due in it, and the estimated balance left after those bills.</p>
              </div>
              <Link to="/app/pay-periods" className="btn-ghost text-sm" data-testid="dashboard-pay-periods-link">See planner →</Link>
            </div>
            {(() => {
              const todayISO = new Date().toISOString().slice(0, 10);
              const future = payPeriods
                .filter((p) => p.period_start > todayISO)
                .slice(0, 3);
              if (!future.length) {
                return <p className="text-sm text-[#59605C]" data-testid="dashboard-pay-periods-empty">No future pay periods to show yet — add a recurring income to see what&rsquo;s ahead.</p>;
              }
              return (
                <div className="grid md:grid-cols-3 gap-4">
                  {future.map((p, i) => (
                    <div key={i} className="p-4 rounded-2xl bg-[#F9F8F6] border-2 border-[#D8CFB9]" data-testid={`dashboard-pp-${i}`}>
                      <StatusBadge status={p.status_label} size="sm" />
                      <div className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Pay period</div>
                      <div className="font-heading text-base text-[#2C302E] leading-snug" data-testid={`dashboard-pp-range-${i}`}>
                        {formatDate(p.period_start)} → {formatDate(p.period_end)}
                      </div>
                      <div className="text-[11px] text-[#8A918D] mt-0.5">Paycheck arrives {formatDate(p.pay_date)}</div>
                      {p.expected_income_breakdown?.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E] mb-0.5">Scheduled income</div>
                          <div className="space-y-0.5">
                            {p.expected_income_breakdown.map((inc, j) => (
                              <button
                                type="button"
                                key={j}
                                className="w-full flex justify-between text-xs hover:bg-[#F1EEE7] rounded px-1 -mx-1 py-0.5 transition-colors"
                                onClick={() => setEditingIncomeEvent({
                                  income_id: inc.income_id,
                                  source: inc.source,
                                  amount: Number(inc.amount || 0),
                                  date: inc.expected_date,
                                  frequency: inc.frequency,
                                })}
                                title="Edit this occurrence or the whole recurring source"
                                data-testid={`dashboard-pp-inc-edit-${i}-${j}`}
                              >
                                <span className="text-[#59605C] truncate mr-2 underline decoration-dotted underline-offset-2">{inc.source}</span>
                                <span className="text-[#3B5B3E]">+{formatMoney(inc.amount)}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-4 space-y-1 text-sm border-t border-[#EBE6E0] pt-3">
                        <MiniRow label="Total scheduled income" value={`+${formatMoney(p.expected_income_amount)}`} pos />
                        <Link
                          to={`/app/bills?payPeriod=${p.pay_date}`}
                          className="flex justify-between text-sm text-[#59605C] hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
                          title="See every bill due in this pay period"
                          data-testid={`dashboard-pp-bills-link-${i}`}
                        >
                          <span>{`Bills due in this period (${p.assigned_bills?.length || 0})`}</span>
                          <span className="text-[#8C3A30]">{`-${formatMoney(p.assigned_bills_total)}`}</span>
                        </Link>
                        {Number(p.assigned_pending_total) > 0 && (
                          <MiniRow
                            label={`Pending charges (${p.assigned_pending?.length || 0})`}
                            value={`-${formatMoney(p.assigned_pending_total)}`}
                          />
                        )}
                        <div className="pt-2 mt-1 border-t border-[#EBE6E0] flex justify-between font-semibold text-[#2C302E]" data-testid={`dashboard-pp-balance-${i}`}>
                          <span>Estimated balance</span>
                          <span className={p.projected_leftover < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]"}>
                            {formatMoney(p.projected_leftover)}
                          </span>
                        </div>
                        <Link
                          to="/app/income?add=1"
                          className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3] transition-colors"
                          title="Add an expected paycheck or one-time deposit"
                          data-testid={`dashboard-pp-add-income-${i}`}
                        >
                          <Plus size={11} /> Add expected income
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {rescheduling && (
        <RescheduleBillModal
          bill={rescheduling}
          onClose={() => setRescheduling(null)}
          onUpdated={(b) => {
            setBills((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...b } : x)));
            refreshForecast();
          }}
          onBulkUpdated={(rows, deletedIds) => {
            setBills((prev) => {
              const deleted = new Set(deletedIds || []);
              const byId = new Map(prev.filter((b) => !deleted.has(b.id)).map((b) => [b.id, b]));
              rows.forEach((r) => byId.set(r.id, { ...(byId.get(r.id) || {}), ...r }));
              return Array.from(byId.values());
            });
            refreshForecast();
          }}
        />
      )}

      {reconciling && (
        <ReconcileBillsFlow
          bills={bills}
          currentPP={(payPeriods || []).find((p) => {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const s = new Date(p.period_start + "T00:00:00");
            const e = new Date(p.period_end + "T00:00:00");
            return s <= today && today <= e;
          }) || (payPeriods || []).find((p) => new Date(p.period_end + "T00:00:00") >= new Date())}
          onClose={() => setReconciling(false)}
          onBillUpdated={(updated) => {
            setBills((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
            refreshForecast();
          }}
          onCompleted={(u) => {
            setUser && setUser(u);
            toast.success("Bills reconciled — see you next week ✨");
            refreshForecast();
          }}
        />
      )}

      {editingIncomeEvent && (
        <EditIncomeEventModal
          event={editingIncomeEvent}
          onClose={() => setEditingIncomeEvent(null)}
          onSaved={() => { setEditingIncomeEvent(null); refreshForecast(); }}
        />
      )}
    </div>
  );
}
